/**
 * Güncel Fiyat Ekranı'ndaki satış fiyatlarını fatura tarafına taşır.
 *
 * Fiyat ekranı (src/pages/guncelfiyatekrani.js) her ürün için `formuller`
 * koleksiyonundaki formülü kullanır:
 *     satış = kaynak.sell * satisCarpan + satisIscilik
 * "Acil" (manuel) mod açıkken ekran tamamen farklı bir formüle geçer. O modda
 * bu tabloyu BİLEREK üretmeyiz — yanlış fiyattan ürün seçilmesindense kural hiç
 * çalışmasın daha güvenli.
 */

/** Türkçe harf katlaması — "Beşli" ile "Besli" aynı anahtara düşsün. */
const fold = (value) =>
  String(value || '')
    .replace(/[ıİ]/g, 'i')
    .replace(/[şŞ]/g, 's')
    .replace(/[ğĞ]/g, 'g')
    .replace(/[üÜ]/g, 'u')
    .replace(/[öÖ]/g, 'o')
    .replace(/[çÇ]/g, 'c')
    .toLowerCase()
    .replace(/[''`´]/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

export const normalizePriceKey = fold;

/** Tek bir formülün güncel satış fiyatı (normal mod). */
export const computeFormulSellPrice = (formul, priceByCode = {}) => {
  if (!formul) return 0;
  if (formul.isManual) {
    const manual = Number(formul.manualSell);
    return Number.isFinite(manual) && manual > 0 ? manual : 0;
  }
  const source = priceByCode[String(formul.kaynak || '').toUpperCase()];
  const base = Number(source?.sell) || 0;
  if (!(base > 0)) return 0;
  const carpan = Number(formul.satisCarpan);
  if (!Number.isFinite(carpan) || carpan <= 0) return 0;
  const iscilik = Number(formul.satisIscilik) || 0;
  const value = base * carpan + iscilik;
  return Number.isFinite(value) && value > 0 ? Math.round(value * 100) / 100 : 0;
};

/**
 * Tek bir formülün güncel ALIŞ fiyatı.
 *
 * Gider pusulası alım belgesidir; ürün/miktar hesabı satış değil ALIŞ
 * fiyatından yapılır (fiyat ekranındaki formülün aynısı:
 * kaynak.buy * alisCarpan + alisIscilik).
 */
export const computeFormulBuyPrice = (formul, priceByCode = {}) => {
  if (!formul) return 0;
  if (formul.isManual) {
    const manual = Number(formul.manualBuy);
    return Number.isFinite(manual) && manual > 0 ? manual : 0;
  }
  const source = priceByCode[String(formul.kaynak || '').toUpperCase()];
  const base = Number(source?.buy) || 0;
  if (!(base > 0)) return 0;
  const carpan = Number(formul.alisCarpan);
  if (!Number.isFinite(carpan) || carpan <= 0) return 0;
  const iscilik = Number(formul.alisIscilik) || 0;
  const value = base * carpan + iscilik;
  return Number.isFinite(value) && value > 0 ? Math.round(value * 100) / 100 : 0;
};

/** { katlanmış ürün adı → güncel ALIŞ fiyatı }. Acil modda bilerek boş döner. */
export const buildLiveProductBuyPriceTable = (formuller, prices, genelAyarlar = null) => {
  if (genelAyarlar?.aktif) return {};
  const byCode = {};
  (prices || []).forEach((p) => {
    const code = String(p?.currencyCode || '').toUpperCase();
    if (code) byCode[code] = p;
  });
  const table = {};
  (formuller || []).forEach((f) => {
    const key = fold(f?.ad);
    if (!key) return;
    const buy = computeFormulBuyPrice(f, byCode);
    if (buy > 0) table[key] = buy;
  });
  return table;
};

/**
 * { katlanmış ürün adı → güncel satış fiyatı }
 * @param {Array} formuller  `formuller` koleksiyonu
 * @param {Array} prices     PriceContext akışı
 * @param {Object|null} genelAyarlar  ayarlar/genel_durum (acil mod bayrağı)
 */
export const buildLiveProductPriceTable = (formuller, prices, genelAyarlar = null) => {
  if (genelAyarlar?.aktif) return {};
  const byCode = {};
  (prices || []).forEach((p) => {
    const code = String(p?.currencyCode || '').toUpperCase();
    if (code) byCode[code] = p;
  });
  const table = {};
  (formuller || []).forEach((f) => {
    const key = fold(f?.ad);
    if (!key) return;
    const sell = computeFormulSellPrice(f, byCode);
    if (sell > 0) table[key] = sell;
  });
  return table;
};

/**
 * Katalog ürün adı → güncel satış fiyatı.
 * Önce birebir, sonra "içeren" eşleşme (en kısa aday kazanır):
 * katalogdaki "Cumhuriyet Ata 2.5'lu" ↔ formüldeki "Cumhuriyet Ata 2.5'lu Gremse".
 */
export const lookupLiveProductPrice = (productName, table = {}) => {
  const key = fold(productName);
  if (!key) return 0;
  if (table[key] > 0) return table[key];

  let best = 0;
  let bestLen = Infinity;
  Object.keys(table).forEach((candidate) => {
    if (!candidate.includes(key) && !key.includes(candidate)) return;
    if (candidate.length < bestLen) {
      bestLen = candidate.length;
      best = table[candidate];
    }
  });
  return best > 0 ? best : 0;
};

/* ------------------------------------------------------------------ *
 * Modül önbelleği
 *
 * Taslak üretimi (invoiceDraftUtils) React ağacının dışından da çağrılıyor
 * (otomatik eşleştirme servisi). Fiyat tablosunu ayarlar nesnesine her yerde
 * taşımak yerine, fiyatları gören ekran tabloyu buraya yazar; taslak üretimi
 * ayarlarda tablo yoksa buradan okur.
 * ------------------------------------------------------------------ */

let cachedTable = {};
let cachedBuyTable = {};

export const setLiveProductPriceTable = (table) => {
  cachedTable = table && typeof table === 'object' ? table : {};
};

export const getLiveProductPriceTable = () => cachedTable;

export const setLiveProductBuyPriceTable = (table) => {
  cachedBuyTable = table && typeof table === 'object' ? table : {};
};

export const getLiveProductBuyPriceTable = () => cachedBuyTable;

/** Ayarlarda alış tablosu varsa onu, yoksa önbelleği kullan. */
export const resolveLiveProductBuyPrices = (settings = null) => {
  const fromSettings = settings?.liveProductBuyPrices;
  if (fromSettings && typeof fromSettings === 'object' && Object.keys(fromSettings).length > 0) {
    return fromSettings;
  }
  return cachedBuyTable;
};

/** Ayarlarda tablo varsa onu, yoksa önbelleği kullan. */
export const resolveLiveProductPrices = (settings = null) => {
  const fromSettings = settings?.liveProductPrices;
  if (fromSettings && typeof fromSettings === 'object' && Object.keys(fromSettings).length > 0) {
    return fromSettings;
  }
  return cachedTable;
};
